import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal, unstable_batchedUpdates } from "react-dom";
import { closestCenter, pointerWithin, rectIntersection, DndContext, DragOverlay, getFirstCollision, KeyboardSensor, MouseSensor, TouchSensor, useDroppable, useSensors, useSensor, MeasuringStrategy, defaultDropAnimationSideEffects } from "@dnd-kit/core";
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy, horizontalListSortingStrategy } from "@dnd-kit/sortable";
import { coordinateGetter as multipleContainersCoordinateGetter } from "./multipleContainersKeyboardCoordinates.js"
import { useDebouncedCallback } from "use-debounce";
import { useImmer } from "use-immer";

import LeadDetailsModal from "../Leads/LeadDetailsModal.js";
import useGlobalParams from "../../hooks/useGlobalParams.js";
import useMutateLead from "../../hooks/useMutateLead.js";
import useStatuses from "../../hooks/useStatuses.js";
import useUsers from "../../hooks/useUsers.js";
import { getStatusId } from "../../utils/kanbanUtils.js";

import Toast from "../../common/Toast/Toast.js";
import DroppableKanbanColumn from "./DroppableKanbanColumn.js";
import StatusColumn from './StatusColumn.js';
import BoardItem from "./BoardItem.js";

import './KanbanBoard.scss';

const dropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({ styles: { active: { opacity: "0.5" } } }),
}

export const TRASH_ID = "void";
const PLACEHOLDER_ID = "placeholder";
// const empty = []

export default function KanbanBoard({ adjustScale = false, cancelDrop, columns, handle = false, initialItems, containerStyle, coordinateGetter = multipleContainersCoordinateGetter, getItemStyles = () => ({}), wrapperStyle = () => ({}), minimal = false, modifiers, renderItem, strategy = verticalListSortingStrategy, trashable = true, vertical = false, initialLeads, nextUrls }) {
  const [items, setItems] = useImmer(initialItems);
  const [leads, setLeads] = useImmer(initialLeads);
  const [toggledColumns, setToggledColumns] = useImmer({});
  const [changingLeadStatus, setChangingLeadStatus] = useImmer({});

  const [containers, setContainers] = useState(Object.keys(items))
  const [activeId, setActiveId] = useState(null);
  const [clonedItems, setClonedItems] = useState(null);

  const lastOverId = useRef(null)
  const recentlyMovedToNewContainer = useRef(false);

  const { categoryId, organizationId } = useGlobalParams();
  const { deleteMutation, updateLead } = useMutateLead({ categoryId });

  const [showDetails, setShowDetails] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [defaultStatus, setDefaultStatus] = useState(null);
  const [showDeleteLead, setShowDeleteLead] = useState(false);

  const { data: statuses } = useStatuses({ categoryId });
  const { data: users } = useUsers({ organizationId, categoryId });

  /**
   * Custom collision detection strategy optimized for multiple containers
   *
   * - First, find any droppable containers intersecting with the pointer.
   * - If there are none, find intersecting containers with the active draggable.
   * - If there are no intersecting containers, return the last matched intersection
   *
   */
  const collisionDetectionStrategy = useCallback(
    args => {
      if (activeId && activeId in items) {
        return closestCenter({
          ...args,
          droppableContainers: args.droppableContainers.filter(
            container => container.id in items
          )
        })
      }

      // Start by finding any intersecting droppable
      const pointerIntersections = pointerWithin(args)
      const intersections =
        pointerIntersections.length > 0
          ? // If there are droppables intersecting with the pointer, return those
            pointerIntersections
          : rectIntersection(args)
      let overId = getFirstCollision(intersections, "id")

      if (overId != null) {
        if (overId === TRASH_ID) {
          // If the intersecting droppable is the trash, return early
          // Remove this if you're not using trashable functionality in your app
          return intersections
        }

        if (overId in items) {
          const containerItems = items[overId]

          // If a container is matched and it contains items (columns 'A', 'B', 'C')
          if (containerItems.length > 0) {
            // Return the closest droppable within that container
            overId = closestCenter({
              ...args,
              droppableContainers: args.droppableContainers.filter(
                container =>
                  container.id !== overId &&
                  containerItems.includes(container.id)
              )
            })[0]?.id
          }
        }

        lastOverId.current = overId

        return [{ id: overId }]
      }

      // When a draggable item moves to a new container, the layout may shift
      // and the `overId` may become `null`. We manually set the cached `lastOverId`
      // to the id of the draggable item that was moved to the new container, otherwise
      // the previous `overId` will be returned which can cause items to incorrectly shift positions
      if (recentlyMovedToNewContainer.current) {
        lastOverId.current = activeId
      }

      // If no droppable is matched, return the last match
      return lastOverId.current ? [{ id: lastOverId.current }] : []
    },
    [activeId, items]
  );

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { delay: 120, tolerance: 100 }}),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, { coordinateGetter }),
  );

  const isSortingContainer = activeId ? containers.includes(activeId) : false;
  
  const findContainer = id => {
    if (id in items) {
      return id;
    }

    return Object.keys(items).find(key => items[key].includes(id))
  }

  const getIndex = id => {
    const container = findContainer(id);
    return !container ? -1 : items[container].indexOf(id);
  }

  const onDragCancel = () => {
    if (clonedItems) {
      // Reset items to their original state in case items have been
      // Dragged across containers
      setItems(clonedItems)
    }

    setActiveId(null)
    setClonedItems(null)
  }

  useEffect(() => {
    requestAnimationFrame(() => {
      recentlyMovedToNewContainer.current = false
    })
  }, [items]);

    const onClickItem = lead => {
    console.log('lead:', lead)
    setEditingLead(lead);
    setShowDetails(true);
  }

  const showDeleteToast = () => {
    setShowDeleteLead(true);
    hideDeleteToast();
  }

  const hideDeleteToast = useDebouncedCallback(() => {
    setShowDeleteLead(false);
  }, 5000);


  const handleDelete = lead => {
    deleteMutation.mutate(lead.id);
    showDeleteToast();
    onHideDetails();
  }

  const handleUndo = () => {
    alert("NOT WORKING LOL")
  }

  const onEditLead = () => {
    // handleUpdate();
    onHideDetails();
  }

  const onHideDetails = () => {
    setShowDetails(false);
    setEditingLead(null);
    setDefaultStatus(null);
  }

  const onDragOver = ({ active, over }) => {
    const overId = over?.id

    if (overId == null || overId === TRASH_ID || active.id in items) {
      return
    }

    const overContainer = findContainer(overId)
    const activeContainer = findContainer(active.id)

    if (!overContainer || !activeContainer) {
      return
    }

    if (activeContainer !== overContainer) {
      return;

      // This code allows items to be dropped at a certain order inside the other columns, but for PL we don't want this functionality
      /*
      setItems(items => {
        const activeItems = items[activeContainer]
        const overItems = items[overContainer]
        const overIndex = overItems.indexOf(overId)
        const activeIndex = activeItems.indexOf(active.id)

        let newIndex

        if (overId in items) {
          newIndex = overItems.length + 1
        } else {
          const isBelowOverItem =
            over &&
            active.rect.current.translated &&
            active.rect.current.translated.top >
              over.rect.top + over.rect.height

          const modifier = isBelowOverItem ? 1 : 0

          newIndex = overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
        }

        recentlyMovedToNewContainer.current = true

        return {
          ...items,
          [activeContainer]: items[activeContainer].filter(
            item => item !== active.id
          ),
          [overContainer]: [
            ...items[overContainer].slice(0, newIndex),
            items[activeContainer][activeIndex],
            ...items[overContainer].slice(
              newIndex,
              items[overContainer].length
            )
          ]
        }
      })
      */
    }
  }

  const onDragEnd = ({ active, over }) => {
    // when containers move (not used)
    if (active.id in items && over?.id) {
      setContainers(containers => {
        const activeIndex = containers.indexOf(active.id)
        const overIndex = containers.indexOf(over.id)

        return arrayMove(containers, activeIndex, overIndex)
      })
    }

    const activeContainer = findContainer(active.id)

    if (!activeContainer) {
      setActiveId(null)
      return
    }

    const overId = over?.id

    if (overId == null) {
      setActiveId(null)
      return
    }

    // lead is dragged over trash
    if (overId === TRASH_ID) {
      setItems(items => ({
        ...items,
        [activeContainer]: items[activeContainer].filter(
          id => id !== activeId
        )
      }))
      setActiveId(null)
      return
    }

    // lead is dragged to a new column (unused)
    if (overId === PLACEHOLDER_ID) {
      const newContainerId = getNextContainerId()

      unstable_batchedUpdates(() => {
        setContainers(containers => [...containers, newContainerId])
        setItems(items => ({
          ...items,
          [activeContainer]: items[activeContainer].filter(
            id => id !== activeId
          ),
          [newContainerId]: [active.id]
        }))
        setActiveId(null)
      })
      return
    }

    const overContainer = findContainer(overId)

    // lead was dragged to another status:
    if (overContainer !== activeContainer) {
      setItems(draft => {
        draft[overContainer].unshift(active.id);
        draft[activeContainer].splice(draft[activeContainer].findIndex(item => item === active.id), 1);
      });

      setLeads(draft => {
        draft[overContainer].unshift(draft[activeContainer].find(lead => lead.id === active.id))
        draft[activeContainer].splice(draft[activeContainer].findIndex(lead => lead.id === active.id), 1);
      });

      // the active container might load more leads, when a lead is moved to another column
      // because the infinite loader intersection observer might become visible, causing a race condition.
      // toggle that column so that leads cannot be loaded until the update has been successful on the BE
      const onSettled = () => {
        setChangingLeadStatus(draft => {
          delete draft[activeContainer];
        });
      }

      setChangingLeadStatus(draft => {
        draft[activeContainer] = true;
      });

      updateLead.mutate({ id: active.id, data: { status_id: getStatusId(overContainer) } }, { onSettled });

      setActiveId(null);

      return;
    }

    if (overContainer) {
      const activeIndex = items[activeContainer].indexOf(active.id)
      const overIndex = items[overContainer].indexOf(overId)
      
      if (activeIndex !== overIndex) {
        setItems(draft => {
          const [item] = draft[overContainer].splice(activeIndex, 1);
          draft[overContainer].splice(overIndex, 0, item);
        });
      }
    }

    // @HAO when the dragging has ended, is the time to check if items have moved around and update their order_index on the DB
    setActiveId(null);
  }

  const onChangeLeads = (containerId, dataPages) => {
    setLeads(draft => {
      draft[containerId] = dataPages.flatMap(page => page.data);
    });

    setItems(draft => {
      draft[containerId] = dataPages.flatMap(page => page.data.map(lead => lead.id));
    });
  }

  const onAddLead = containerId => {
    setShowDetails(true);
    setDefaultStatus(getStatusId(containerId));
  }

  const onToggleColumn = containerId => {
    setToggledColumns(draft => {
      if (draft[containerId]) {
        delete draft[containerId];
      }
      else {
        draft[containerId] = true;
      }
    });
  }

  const findLead = (containerId, leadId) => leads[containerId].find(lead => lead.id === leadId);

  return (
    <>
      <LeadDetailsModal statusDisabled={defaultStatus !== null} organizationId={organizationId} categoryId={categoryId} defaultStatus={defaultStatus} show={showDetails} lead={editingLead} onEditLead={onEditLead} onHide={onHideDetails} onDelete={() => handleDelete(editingLead)} />
      <Toast show={showDeleteLead} action='Undo' onHide={() => hideDeleteToast.flush()} onActionClick={handleUndo}>
        Lead moved to trash
      </Toast>

      <DndContext
        sensors={sensors}
        // autoScroll={{ acceleration: 1 }} // TODO -> when dealing with a lot of columns, it scrolls too fast https://github.com/clauderic/dnd-kit/issues/825
        collisionDetection={collisionDetectionStrategy}
        measuring={{ droppable: { strategy: MeasuringStrategy.Always }}}
        onDragStart={({ active }) => {
          setActiveId(active.id)
          setClonedItems(items)
        }}
        onDragOver={onDragOver}
        onDragEnd={onDragEnd}
        cancelDrop={cancelDrop}
        onDragCancel={onDragCancel}
        modifiers={modifiers}
      >
        <div className="__kanban-board">
          <SortableContext items={[...containers, PLACEHOLDER_ID]} strategy={vertical ? verticalListSortingStrategy : horizontalListSortingStrategy }>
            {containers.map(containerId => (
              toggledColumns[containerId]
              ? <div className="collapsed-column" onClick={() => onToggleColumn(containerId)} key={containerId}>
                  <div className="status-color" style={{ backgroundColor: `#${statuses?.find(status => status.id === getStatusId(containerId))?.color}` }} />
                  <div className="status-title">{ statuses?.find(status => status.id === getStatusId(containerId))?.name }</div>
                </div>
              : (
                <DroppableKanbanColumn allowLoadMore={!changingLeadStatus[containerId]} key={containerId} id={containerId} leads={initialLeads[containerId]} nextUrl={nextUrls[containerId]} columns={columns} items={items[containerId]} scrollable style={containerStyle} unstyled={minimal} onRemove={() => handleRemove(containerId)} onChangeLeads={leads => onChangeLeads(containerId, leads)} onAddLead={() => onAddLead(containerId)} onToggleColumn={() => onToggleColumn(containerId)}>
                  <SortableContext items={items[containerId]} strategy={strategy}>
                    { items[containerId].map((value, index) => (
                      <SortableItem disabled={isSortingContainer} key={value} id={value} lead={findLead(containerId, value)} onClickItem={onClickItem} index={index} handle={handle} style={getItemStyles} wrapperStyle={wrapperStyle} renderItem={renderItem} containerId={containerId} getIndex={getIndex} user={users?.find(user => findLead(containerId, value)?.assigned_user_id === user.id)} />
                    )) }
                  </SortableContext>
                </DroppableKanbanColumn>
              )
            ))}
            {/* {minimal ? (
              undefined
            ) : (
              <DroppableContainer
                id={PLACEHOLDER_ID}
                disabled={isSortingContainer}
                items={empty}
                onClick={handleAddColumn}
                placeholder
              >
                + Add column
              </DroppableContainer>
            )} */}
          </SortableContext>
        </div>
        {createPortal(
          <DragOverlay adjustScale={adjustScale} dropAnimation={dropAnimation}>
            {activeId ? (containers.includes(activeId) ? renderContainerDragOverlay(activeId) : renderSortableItemDragOverlay(activeId)) : null }
          </DragOverlay>,
          document.body
        )}
        <Trash visible={activeId && !containers.includes(activeId)} />
      </DndContext>
    </>
  );
  
  function renderSortableItemDragOverlay(id) {
    const style = getItemStyles({ containerId: findContainer(id), overIndex: -1, index: getIndex(id), value: id, isSorting: true, isDragging: true, isDragOverlay: true });
    return <BoardItem lead={findLead(findContainer(id), id)} user={users?.find(user => findLead(findContainer(id), id)?.assigned_user_id === user.id)} value={id} handle={handle} style={style} wrapperStyle={wrapperStyle({ index: 0 })} renderItem={renderItem} dragOverlay />;
  }

  // @HAO -> used only when dragging containers, not used now
  function renderContainerDragOverlay(containerId) {
    return (
      <StatusColumn label={containerId} columns={columns} style={{ height: "100%" }} shadow unstyled={false}>
        {items[containerId].map((item, index) => (
          <BoardItem
            key={item}
            lead={findLead(findContainer(item), item)}
            user={users?.find(user => findLead(findContainer(item), item)?.assigned_user_id === user.id)}
            value={item}
            handle={handle}
            style={getItemStyles({
              containerId,
              overIndex: -1,
              index: getIndex(item),
              value: item,
              isDragging: false,
              isSorting: false,
              isDragOverlay: false
            })}
            wrapperStyle={wrapperStyle({ index })}
            renderItem={renderItem}
          />
        ))}
      </StatusColumn>
    )
  }

  function handleRemove(containerID) {
    setContainers(containers => containers.filter(id => id !== containerID))
  }

  // function handleAddColumn() {
  //   const newContainerId = getNextContainerId()

  //   unstable_batchedUpdates(() => {
  //     setContainers(containers => [...containers, newContainerId])
  //     setItems(items => ({
  //       ...items,
  //       [newContainerId]: []
  //     }))
  //   })
  // }

  function getNextContainerId() {
    const containerIds = Object.keys(items)
    const lastContainerId = containerIds[containerIds.length - 1]

    return String.fromCharCode(lastContainerId.charCodeAt(0) + 1)
  }
}

function Trash({ visible }) {
  const { setNodeRef, isOver } = useDroppable({ id: TRASH_ID });

  // TODO -> move to class
  const style = {
    display        : "flex",
    alignItems     : "center",
    justifyContent : "center",
    width          : 300,
    padding: 32,
    borderRadius   : 5,
    border         : "1px solid",
    borderColor    : isOver ? "red" : "#DDD"
  }

  return (
    <div className="trash-container" style={{ visibility: !visible && 'hidden', display: "none" }}>
      <div className="trash" ref={setNodeRef} style={style}>Drop here to delete</div>
    </div>
  );
}

function SortableItem({ disabled, id, index, handle, renderItem, style, containerId, getIndex, wrapperStyle, lead, onClickItem, user }) {
  const { setNodeRef, setActivatorNodeRef, listeners, isDragging, isSorting, over, overIndex, transform, transition } = useSortable({ id, data: { containerId } });
  const mounted = useMountStatus();
  const mountedWhileDragging = isDragging && !mounted;

  const itemStyle = style({ index, value: id, isDragging, isSorting, overIndex: over ? getIndex(over.id) : overIndex, containerId });

  return (
    <BoardItem ref={disabled ? undefined : setNodeRef} value={id} lead={lead} onClickItem={onClickItem} dragging={isDragging} sorting={isSorting} handle={handle} handleProps={handle ? { ref: setActivatorNodeRef } : undefined} index={index} wrapperStyle={wrapperStyle({ index })} style={itemStyle} transition={transition} transform={transform} fadeIn={mountedWhileDragging} listeners={listeners} renderItem={renderItem} user={user} />
  );
}

function useMountStatus() {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => setIsMounted(true), 500);

    return () => clearTimeout(timeout);
  }, []);

  return isMounted;
}
